/**
 * Thin API wrappers for the teacher publish-to-Canvas-via-Clever endpoints.
 *
 * All three call the matching routes on `/classrooms/:id/lessons/:id/clever-publish`.
 * The write endpoints may return 503 when the backend has no
 * `CLEVER_API_BEARER_TOKEN` configured; we surface that as a typed
 * `CleverNotConfiguredError` so the modal/badge can render the dedicated
 * "not configured" state instead of a generic failure toast.
 */

import api from "@/utils/axios-utils";
import type { CleverLessonAssignment } from "../types/clever";

export class CleverNotConfiguredError extends Error {
  constructor(message = "Clever LMS Connect not configured") {
    super(message);
    this.name = "CleverNotConfiguredError";
  }
}

export interface PublishToCleverRequest {
  due_at?: string | null;
  max_points?: number;
  max_attempts?: number;
}

export interface PublishToCleverResponse {
  sync_status: string;
  clever_assignment_id: string | null;
  local_assignment_created: boolean;
}

const publishPath = (classroomId: number, lessonId: number): string =>
  `/classrooms/${classroomId}/lessons/${lessonId}/clever-publish`;

function isNotConfigured(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 503;
}

export async function publishToClever(
  classroomId: number,
  lessonId: number,
  body: PublishToCleverRequest = {},
): Promise<PublishToCleverResponse> {
  try {
    const response = await api.post<PublishToCleverResponse>(
      publishPath(classroomId, lessonId),
      body,
    );
    return response.data;
  } catch (error) {
    if (isNotConfigured(error)) {
      throw new CleverNotConfiguredError();
    }
    throw error;
  }
}

export async function unpublishFromClever(
  classroomId: number,
  lessonId: number,
): Promise<PublishToCleverResponse> {
  try {
    const response = await api.delete<PublishToCleverResponse>(
      publishPath(classroomId, lessonId),
    );
    return response.data;
  } catch (error) {
    if (isNotConfigured(error)) {
      throw new CleverNotConfiguredError();
    }
    throw error;
  }
}

export async function getCleverPublishState(
  classroomId: number,
  lessonId: number,
): Promise<CleverLessonAssignment | null> {
  try {
    const response = await api.get<CleverLessonAssignment>(
      publishPath(classroomId, lessonId),
    );
    return response.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    // 404 is the "no local row, never published" signal. Everything else
    // should propagate so the caller can decide whether to show an error.
    if (status === 404) {
      return null;
    }
    throw error;
  }
}
